import React, { useState, useEffect, useRef, useReducer, useCallback, memo, useContext } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Calendar, 
  MapPin, 
  Briefcase, 
  Edit2, 
  Save, 
  X, 
  Loader2,
  Phone,
  Clock,
  Building
} from 'lucide-react';
import userProfileService from '../../services/userProfileService';
import PageHeader from '../Shared/PageHeader';
import { AuthContext } from '../../contexts/AuthContext';

// List of all countries with priority countries at the top
const COUNTRIES = [
  // Priority countries
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  // Separator
  { code: 'divider', name: '──────────' },
  // Other countries
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  // ... add needed countries alphabetically
];

const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        [action.field]: action.value
      };
    case 'RESET':
      return action.values;
    default:
      return state;
  }
};

const MemoizedEditableInput = memo(({ name, value, reference, onChange, type = 'text' }) => {
  const handleChange = useCallback((e) => {
    e.stopPropagation();
    onChange(name, e.target.value);
  }, [name, onChange]);

  if (type === 'country') {
    return (
      <select
        ref={reference}
        name={name}
        value={value}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        className="input h-8 flex-1 px-2"
      >
        <option value="">Select a country</option>
        {COUNTRIES.map((country) => (
          country.code === 'divider' ? (
            <option key={country.code} disabled>{country.name}</option>
          ) : (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          )
        ))}
      </select>
    );
  }

  return (
    <input
      ref={reference}
      type="text"
      name={name}
      value={value}
      onChange={handleChange}
      onFocus={(e) => {
        e.stopPropagation();
        e.target.select();
      }}
      onClick={(e) => e.stopPropagation()}
      className="input h-8 flex-1 px-2"
      autoComplete="off"
    />
  );
});

MemoizedEditableInput.displayName = 'MemoizedEditableInput';

const MemoizedInfoItem = memo(({ icon: Icon, label, name, value, editable, inputRef, onChange, isEditing, type }) => {
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (editable && isEditing) {
      inputRef?.current?.focus();
    }
  }, [editable, isEditing, inputRef]);

  // Function to display country name instead of code
  const displayValue = useCallback((val) => {
    if (type === 'country' && val) {
      const country = COUNTRIES.find(c => c.code === val);
      return country ? country.name : val;
    }
    return val;
  }, [type]);

  return (
    <div className="flex items-center gap-3 py-2" onClick={handleClick}>
      <Icon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}:</span>
      {isEditing && editable ? (
        <MemoizedEditableInput
          name={name}
          value={value}
          reference={inputRef}
          onChange={onChange}
          type={type}
        />
      ) : (
        <span className="text-sm text-gray-900 dark:text-gray-100">{displayValue(value) || 'N/A'}</span>
      )}
    </div>
  );
});

MemoizedInfoItem.displayName = 'MemoizedInfoItem';

const UserProfile = ({ embedded = false }) => {
  console.log('UserProfile component rendering');

  const { user } = useContext(AuthContext);
  // TODO: Extend to check for specific permissions (e.g., 'manage_enhanced_profile')
  // For now, only superusers see enhanced fields (phone, timezone, institution)
  const isSuperuser = user?.is_superuser || false;

  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [formData, dispatch] = useReducer(formReducer, {
    first_name: '',
    last_name: '',
    country: '',
    phone_number: '',
    timezone: '',
    institution_name: ''
  });

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const countryRef = useRef(null);
  const phoneRef = useRef(null);
  const timezoneRef = useRef(null);
  const institutionRef = useRef(null);


  // Load initial profile data
  useEffect(() => {
    console.log('Component mounted');
    const fetchProfile = async () => {
      console.log('Fetching profile data...');
      try {
        const userData = await userProfileService.getUserProfile();
        console.log('Received profile data:', userData);
        
        // Update both profile and form data
        setProfile(userData);
        
        // Extract profile fields from userData.profile
        const profileData = userData.profile || {};
        dispatch({
          type: 'RESET',
          values: {
            first_name: profileData.first_name || '',
            last_name: profileData.last_name || '',
            country: profileData.country || '',
            phone_number: profileData.phone_number || '',
            timezone: profileData.timezone || '',
            institution_name: profileData.institution_name || ''
          }
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('An error occurred while fetching the data.');
      }
    };

    fetchProfile();
    return () => {
      console.log('Component unmounting');
    };
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const handleSubmit = async (e) => {
    console.log('handleSubmit called');
    e.preventDefault();
    try {
      console.log('Submitting form data:', formData);
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const updatedProfile = await userProfileService.updateUserProfile(formData);
      console.log('Received updated profile:', updatedProfile);
      
      // Update both profile and form data with the response
      setProfile(updatedProfile);
      const profileData = updatedProfile.profile || {};
      dispatch({
        type: 'RESET',
        values: {
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          country: profileData.country || '',
          phone_number: profileData.phone_number || '',
          timezone: profileData.timezone || '',
          institution_name: profileData.institution_name || ''
        }
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.status === 401) {
        // Handle authentication error
        setError('Please log in again to update your profile.');
        // Optionally redirect to login
        // window.location.href = '/login';
      } else {
        setError('An error occurred while updating the profile.');
      }
    }
  };

  const handleEditToggle = useCallback(() => {
    console.log('handleEditToggle called, current isEditing:', isEditing);
    if (isEditing) {
      console.log('Resetting form data to profile values');
      const profileData = profile.profile || {};
      dispatch({
        type: 'RESET',
        values: {
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          country: profileData.country || '',
          phone_number: profileData.phone_number || '',
          timezone: profileData.timezone || '',
          institution_name: profileData.institution_name || ''
        }
      });
    }
    setIsEditing(!isEditing);
    setError('');
  }, [isEditing, profile]);

  if (error) {
    console.log('Error occurred:', error);
    return (
      <div className={embedded ? "p-8" : "flex justify-center items-center h-screen"}>
        <div className={embedded ? "" : "card p-8"}>
          <h1 className="mb-2 text-base font-semibold text-danger-600 dark:text-danger-400">Error</h1>
          <p className="text-sm text-gray-700 dark:text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    console.log('Profile data not available yet');
    return (
      <div className={embedded ? "flex justify-center items-center py-16" : "flex justify-center items-center h-screen"}>
        <div className="flex flex-col items-center">
          <Loader2 className="mb-3 h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  console.log('Rendering profile data:', profile);

  // Embedded version - clean styling
  if (embedded) {
    return (
      <div className="p-6">
        {/* Header with Edit Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Personal Information</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Update your personal details</p>
          </div>
          <button 
            onClick={handleEditToggle}
            className={`btn self-start sm:self-auto ${isEditing ? 'btn-secondary' : 'btn-primary'}`}
          >
            {isEditing ? (
              <><X className="h-4 w-4" />Cancel</>
            ) : (
              <><Edit2 className="h-4 w-4" />Edit</>
            )}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Profile Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="label">First Name</label>
              {isEditing ? (
                <input
                  ref={firstNameRef}
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleFieldChange('first_name', e.target.value)}
                  className="input"
                />
              ) : (
                <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">{formData.first_name || '—'}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="label">Last Name</label>
              {isEditing ? (
                <input
                  ref={lastNameRef}
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleFieldChange('last_name', e.target.value)}
                  className="input"
                />
              ) : (
                <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">{formData.last_name || '—'}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="label">Email Address</label>
              <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">{profile.email}</p>
            </div>

            {/* Role */}
            <div>
              <label className="label">Role</label>
              <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">{profile.role || 'User'}</p>
            </div>

            {/* Country */}
            <div>
              <label className="label">Country</label>
              {isEditing ? (
                <select
                  ref={countryRef}
                  name="country"
                  value={formData.country}
                  onChange={(e) => handleFieldChange('country', e.target.value)}
                  className="input"
                >
                  <option value="">Select a country</option>
                  {COUNTRIES.map((country) => (
                    country.code === 'divider' ? (
                      <option key={country.code} disabled>{country.name}</option>
                    ) : (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    )
                  ))}
                </select>
              ) : (
                <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">
                  {formData.country ? COUNTRIES.find(c => c.code === formData.country)?.name || formData.country : '—'}
                </p>
              )}
            </div>

            {/* Member Since */}
            <div>
              <label className="label">Member Since</label>
              <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">
                {new Date(profile.date_joined).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>

          {/* Enhanced Profile Fields - Superuser Only */}
          {isSuperuser && (
            <div className="mt-8 border-t border-gray-200 pt-6 dark:border-gray-700">
              <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Phone */}
                <div>
                  <label className="label">Phone Number</label>
                  {isEditing ? (
                    <input
                      ref={phoneRef}
                      type="text"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">{formData.phone_number || '—'}</p>
                  )}
                </div>

                {/* Timezone */}
                <div>
                  <label className="label">Timezone</label>
                  {isEditing ? (
                    <input
                      ref={timezoneRef}
                      type="text"
                      name="timezone"
                      value={formData.timezone}
                      onChange={(e) => handleFieldChange('timezone', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">{formData.timezone || '—'}</p>
                  )}
                </div>

                {/* Institution */}
                <div className="md:col-span-2">
                  <label className="label">Institution</label>
                  {isEditing ? (
                    <input
                      ref={institutionRef}
                      type="text"
                      name="institution_name"
                      value={formData.institution_name}
                      onChange={(e) => handleFieldChange('institution_name', e.target.value)}
                      className="input"
                    />
                  ) : (
                    <p className="break-words rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-900 dark:bg-gray-900/60 dark:text-gray-100">{formData.institution_name || '—'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isEditing && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="mt-6 flex justify-end border-t border-gray-100 pt-5 dark:border-gray-700"
            >
              <button
                type="submit"
                className="btn btn-primary"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </button>
            </motion.div>
          )}
        </form>
      </div>
    );
  }

  // Standalone page version
  return (
    <div>
      <div>
        <PageHeader icon={User} title="User Profile" />

        <div className="card overflow-hidden">
            <div className="card-band">
              <h2 className="card-band-title">Personal Information</h2>
              <button 
                onClick={handleEditToggle}
                className="btn btn-secondary btn-sm"
              >
                {isEditing ? (
                  <><X className="h-4 w-4" />Cancel</>
                ) : (
                  <><Edit2 className="h-4 w-4" />Edit Profile</>
                )}
              </button>
            </div>
            <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-4">
                  <MemoizedInfoItem 
                    icon={User}
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    editable={true}
                    inputRef={firstNameRef}
                    onChange={handleFieldChange}
                    isEditing={isEditing}
                  />
                  <MemoizedInfoItem 
                    icon={User}
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    editable={true}
                    inputRef={lastNameRef}
                    onChange={handleFieldChange}
                    isEditing={isEditing}
                  />
                  <MemoizedInfoItem 
                    icon={Mail}
                    label="Email"
                    name="email"
                    value={profile.email}
                    editable={false}
                    isEditing={isEditing}
                  />
                </div>
                <div className="space-y-4">
                  <MemoizedInfoItem 
                    icon={Briefcase}
                    label="Role"
                    name="role"
                    value={profile.role}
                    editable={false}
                    isEditing={isEditing}
                  />
                  <MemoizedInfoItem 
                    icon={MapPin}
                    label="Country"
                    name="country"
                    value={formData.country}
                    editable={true}
                    inputRef={countryRef}
                    onChange={handleFieldChange}
                    isEditing={isEditing}
                    type="country"
                  />
                  <MemoizedInfoItem 
                    icon={Calendar}
                    label="Joined"
                    name="date_joined"
                    value={new Date(profile.date_joined).toLocaleDateString('en-US', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                    editable={false}
                    isEditing={isEditing}
                  />
                </div>
              </div>

              {/* Enhanced Profile Fields - Superuser Only */}
              {isSuperuser && (
                <div className="mt-8 border-t border-gray-200 pt-8 dark:border-gray-700">
                  <div className="mb-4">
                    <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                      <Building className="h-[18px] w-[18px] text-primary-600 dark:text-primary-400" />
                      Additional Information
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Enhanced profile options for administrators</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className="space-y-4">
                      <MemoizedInfoItem 
                        icon={Phone}
                        label="Phone Number"
                        name="phone_number"
                        value={formData.phone_number}
                        editable={true}
                        inputRef={phoneRef}
                        onChange={handleFieldChange}
                        isEditing={isEditing}
                      />
                      <MemoizedInfoItem 
                        icon={Building}
                        label="Institution"
                        name="institution_name"
                        value={formData.institution_name}
                        editable={true}
                        inputRef={institutionRef}
                        onChange={handleFieldChange}
                        isEditing={isEditing}
                      />
                    </div>
                    <div className="space-y-4">
                      <MemoizedInfoItem 
                        icon={Clock}
                        label="Timezone"
                        name="timezone"
                        value={formData.timezone}
                        editable={true}
                        inputRef={timezoneRef}
                        onChange={handleFieldChange}
                        isEditing={isEditing}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isEditing && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="mt-6"
                >
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-full"
                  >
                    <Save className="h-4 w-4" />
                    Save Changes
                  </button>
                </motion.div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;